// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Modal + toast infrastructure and the static modal content (How it works,
 * Privacy / threat model, About). The modal host is hidden with the `hidden`
 * attribute and the global `[hidden]{display:none!important}` guard, so no
 * invisible overlay can ever swallow taps.
 */
import { icon } from './icons.ts';

let hostEl: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function initModals(): void {
  const host = document.createElement('div');
  host.id = 'modal-host';
  host.hidden = true;
  host.innerHTML = `<div class="modal-backdrop" data-close></div><div class="modal" role="dialog" aria-modal="true"></div>`;
  document.body.appendChild(host);
  hostEl = host;
  host.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close !== undefined || t.closest('[data-close]')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hostEl && !hostEl.hidden) closeModal();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!hostEl) return;
  lastFocus = document.activeElement as HTMLElement;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="modal-title">${escapeHtml(title)}</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close">${icon('x')}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>`;
  hostEl.hidden = false;
  const close = modal.querySelector('[data-close]') as HTMLElement | null;
  close?.focus();
}

export function closeModal(): void {
  if (!hostEl) return;
  hostEl.hidden = true;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = '';
  lastFocus?.focus?.();
}

export function isModalOpen(): boolean {
  return !!hostEl && !hostEl.hidden;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el?.classList.remove('show'), 2600);
}

function g(term: string, label?: string): string {
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button">${escapeHtml(label ?? term)}</span>`;
}

export function howItWorksHtml(): string {
  return `
    <ol class="steps">
      <li><span class="step-n">1</span><div><strong>Open a room, share the link.</strong>
        Chatwell mints a random room id and a four-word ${g('passphrase', 'passphrase')}. Both live only in the link’s <code>#hash</code>, which browsers never send to a server. Send that link to whoever you want in the room.</div></li>
      <li><span class="step-n">2</span><div><strong>Everyone joins from the link.</strong>
        Each person opens it and picks a display name. No account, no app, no install.</div></li>
      <li><span class="step-n">3</span><div><strong>Browsers connect in a ${g('full mesh', 'full mesh')}.</strong>
        Each browser finds the others through a public ${g('signalling relay', 'signalling relay')} — used once, for the handshake only — then opens direct ${g('data channel', 'WebRTC data channels')} with ${g('stun', 'STUN')}. No server sits in the message path.</div></li>
      <li><span class="step-n">4</span><div><strong>Confirm the four words.</strong>
        Everyone sees the same ${g('sas', 'four-word code')}. If they match, no impostor slipped into the middle of the room.</div></li>
      <li><span class="step-n">5</span><div><strong>Chat.</strong>
        Type a message or drop an image. Each one is sealed with ${g('aes-gcm', 'AES-GCM-256')} before it leaves and opened only inside the other participants’ tabs. Typing indicators and who’s here update live.</div></li>
      <li><span class="step-n">6</span><div><strong>It evaporates.</strong>
        Nothing is stored on a server — the room is ${g('ephemeral', 'ephemeral')}. When everyone closes the tab, it’s gone. Want a copy? Export a plain-text transcript to your device before you leave.</div></li>
    </ol>
    <p class="muted small">Works in any modern browser. If two people are both behind strict routers with no path between them, a connection can occasionally fail — see the Privacy note on ${g('symmetric nat', 'symmetric NAT')}.</p>`;
}

export function privacyHtml(): string {
  return `
    <div class="tm">
      <section><h3 class="tm-good">Protected</h3><ul>
        <li>The <strong>contents</strong> of every message — text and inline images — travel over direct ${g('data channel', 'data channels')} between the participants’ browsers, ${g('dtls', 'DTLS')}-encrypted, and are additionally sealed in ${g('aes-gcm', 'AES-GCM-256')} with a key derived from the room ${g('passphrase', 'passphrase')}. No server is ever in the message path — the plaintext exists only inside the participants’ tabs.</li>
        <li>The <strong>${g('passphrase', 'passphrase')}</strong> encrypts the ${g('signalling relay', 'signalling')} handshake and derives both the message key and the ${g('sas', 'four verification words')}. It lives only in the link’s hash and never reaches any server.</li>
        <li><strong>Nothing is stored server-side, ever.</strong> No account, no cookies, no third-party fonts, no history in the cloud. The room is ${g('ephemeral', 'ephemeral')}: messages live only in each tab’s memory.</li>
      </ul></section>
      <section><h3 class="tm-bad">Not protected</h3><ul>
        <li>Peer discovery uses <strong>public nostr relays</strong>. They learn an ephemeral room id and that browsers are connecting, but the handshake is encrypted with your passphrase — they can’t read it or your messages.</li>
        <li>Your <strong>IP address</strong> is visible to the other participants and to the public ${g('stun', 'STUN')} server — inherent to any peer-to-peer session.</li>
        <li><strong>No ${g('forward secrecy', 'forward secrecy')} in v1.</strong> A single static key derives from the passphrase, so anyone who later learns the passphrase and recorded the ciphertext could read it. Use a fresh room for anything sensitive.</li>
        <li>NAT traversal uses public STUN only. A ${g('symmetric nat', 'symmetric-NAT')} pair with no relay may fail to connect. We won’t add a relay because it would be a backend that could see traffic.</li>
        <li>Ephemerality protects against a <strong>server</strong> keeping a copy — not against a <strong>person in the room</strong> screenshotting or exporting the transcript.</li>
      </ul></section>
      <section><h3 class="tm-trust">Trust surface</h3><ul>
        <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain to it.</li>
        <li>The public signalling relays and STUN server (encrypted handshake only; they never carry your messages).</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your messages are never sent to it.</li>
        <li>Feedback you choose to send goes to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your messages are never sent.</li>
      </ul></section>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <p>Chatwell is a disposable, end-to-end encrypted group chat room you open from a link. Share the link, everyone joins with a name, and messages go straight browser-to-browser over an encrypted mesh — no account, no app, no server keeping a copy. When everyone leaves, the room is gone.</p>
    <p>It’s part of a small collection of privacy-first, zero-backend browser tools. Everything happens on your device.</p>
    <p class="about-links">
      <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      <a href="https://github.com/ben-gy/chatwell" target="_blank" rel="noopener">source on GitHub</a>
    </p>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
